import axios from "axios";
import React, { useState, useEffect } from "react";
import swal from "sweetalert";
import { AiOutlineUser, AiOutlineMail, AiOutlineLock } from "react-icons/ai";
import { FaMobileAlt, FaAddressCard } from "react-icons/fa";
import { MdDateRange } from "react-icons/md";

const AddUser = () => {
  const apiUrl = process.env.REACT_APP_API_URL;
  const [editedUser, setEditedUser] = useState({
    firstName: "",
    lastName: "",
    mobile: "",
    email: "",
    roleId: "",
    status: true,
    password: "",
  });
  const [roles, setRoles] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    fetchRoles();
  }, []);

  const fetchRoles = async () => {
    try {
      const response = await axios.get(`${apiUrl}/userRole`, {
        headers: { loginToken: localStorage.getItem('loginToken') }
      });
      const data = response.data;
      setRoles(data);
      // Auto-select Admin role so new users get full access by default
      const adminRole = data.find(r => r.roleName?.toLowerCase() === 'admin');
      if (adminRole) {
        setEditedUser(prev => ({ ...prev, roleId: String(adminRole.id) }));
      }
    } catch (error) {
      console.error('Error fetching roles:', error);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setEditedUser((prevState) => ({
      ...prevState,
      [name]: value,
    }));
  };

  const handleSubmit = () => {
    // Validation logic goes here
    if (
      editedUser.firstName.trim() === "" ||
      editedUser.lastName.trim() === "" ||
      editedUser.mobile.trim() === "" ||
      editedUser.email.trim() === "" ||
      editedUser.roleId.trim() === "" ||
      editedUser.password.trim() === "" // Check if password is empty
    ) {
      alert("Please fill in all fields.");
      return;
    }

    // Check if the email is valid
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(editedUser.email)) {
      alert("Please enter a valid email address.");
      return;
    }

    // Check if the password meets the minimum length requirement
    if (editedUser.password.length < 6) {
      alert("Password must be at least 6 characters long.");
      return;
    }
    console.log(editedUser)
    setLoading(true);
    // If all fields are filled and password is valid, proceed with saving
    axios
      .post(`${apiUrl}/user/addUser`, editedUser)
      .then((res) => {
        if (res.data) {
          swal({
            icon: "success",
            title: "Success",
            text: "Added successfully",
          });
          onCancel(); // Reset form after success
        }
      })
      .catch((err) => {
        if (err.response) {
          swal({
            title: "Error",
            icon: "error",
            text: err.response?.data?.error,
          });
        } else {
          console.log(err);
        }
      })
      .finally(() => {
        setLoading(false);
      });
  };

  const onCancel = () => {
    // Cancel logic goes here
    setEditedUser({
      firstName: "",
      lastName: "",
      mobile: "",
      email: "",
      roleId: "",
      status: true,
      password: "",
    });
  };

  return (
    <div className="w-full mx-5">
      <div className="">
        <div className="p-4 w-full bg-white rounded-lg shadow-md">
          <h2 className="text-xl font-bold text-gray-800 mb-3">Personal Information</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="mb-3">
              <label
                htmlFor="firstName"
                className="block text-gray-700 text-sm font-semibold mb-2"
              >
                <AiOutlineUser className="inline mr-2" /> First Name:
              </label>
              <input
                type="text"
                id="firstName"
                name="firstName"
                value={editedUser.firstName}
                onChange={handleInputChange}
                className="shadow-md border rounded-lg w-full py-2 px-4 text-gray-700 focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
            <div className="mb-3">
              <label
                htmlFor="lastName"
                className="block text-gray-700 text-sm font-semibold mb-2"
              >
                <AiOutlineUser className="inline mr-2" /> Last Name:
              </label>
              <input
                type="text"
                id="lastName"
                name="lastName"
                value={editedUser.lastName}
                onChange={handleInputChange}
                className="shadow-md border rounded-lg w-full py-2 px-4 text-gray-700 focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
            <div className="mb-3">
              <label
                htmlFor="mobile"
                className="block text-gray-700 text-sm font-semibold mb-2"
              >
                <FaMobileAlt className="inline mr-2" /> Mobile:
              </label>
              <input
                type="text"
                id="mobile"
                name="mobile"
                value={editedUser.mobile}
                onChange={handleInputChange}
                className="shadow-md border rounded-lg w-full py-2 px-4 text-gray-700 focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
            <div className="mb-3">
              <label
                htmlFor="email"
                className="block text-gray-700 text-sm font-semibold mb-2"
              >
                <AiOutlineMail className="inline mr-2" /> Email:
              </label>
              <input
                type="email"
                id="email"
                name="email"
                value={editedUser.email}
                onChange={handleInputChange}
                className="shadow-md border rounded-lg w-full py-2 px-4 text-gray-700 focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
          </div>

          <h2 className="text-xl font-bold text-gray-800 mb-3 mt-4">Account Details</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="mb-3">
              <label
                htmlFor="roleId"
                className="block text-gray-700 text-sm font-semibold mb-2"
              >
                User Role
              </label>
              <select
                id="roleId"
                name="roleId"
                value={editedUser.roleId}
                onChange={handleInputChange}
                className="shadow-md border rounded-lg w-full py-2 px-4 text-gray-700 focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="">Select Role</option>
                {roles.map(role => (
                  <option key={role.id} value={role.id}>
                    {role.roleName}
                  </option>
                ))}
              </select>
            </div>
            <div className="mb-3">
              <label
                htmlFor="password"
                className="block text-gray-700 text-sm font-semibold mb-2"
              >
                <AiOutlineLock className="inline mr-2" /> Password:
              </label>
              <input
                type="password"
                id="password"
                name="password"
                value={editedUser.password}
                onChange={handleInputChange}
                className="shadow-md border rounded-lg w-full py-2 px-4 text-gray-700 focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
          </div>

          <h2 className="text-xl font-bold text-gray-800 mb-3 mt-4">Additional Information</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="mb-3">
              <label
                htmlFor="address"
                className="block text-gray-700 text-sm font-semibold mb-2"
              >
                <FaAddressCard className="inline mr-2" /> Address:
              </label>
              <input
                type="text"
                id="address"
                name="address"
                value={editedUser.address || ""}
                onChange={handleInputChange}
                className="shadow-md border rounded-lg w-full py-2 px-4 text-gray-700 focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
            <div className="mb-3">
              <label
                htmlFor="dob"
                className="block text-gray-700 text-sm font-semibold mb-2"
              >
                <MdDateRange className="inline mr-2" /> Date of Birth:
              </label>
              <input
                type="date"
                id="dob"
                name="dob"
                value={editedUser.dob || ""}
                onChange={handleInputChange}
                className="shadow-md border rounded-lg w-full py-2 px-4 text-gray-700 focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
            <div className="mb-3">
              <label
                htmlFor="gender"
                className="block text-gray-700 text-sm font-semibold mb-2"
              >
                Gender:
              </label>
              <select
                id="gender"
                name="gender"
                value={editedUser.gender || ""}
                onChange={handleInputChange}
                className="shadow-md border rounded-lg w-full py-2 px-4 text-gray-700 focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="">Select Gender</option>
                <option value="male">Male</option>
                <option value="female">Female</option>
                <option value="other">Other</option>
              </select>
            </div>
          </div>

          <div className="bg-gray-50 px-4 py-3 sm:px-6 sm:flex mt-4">
            <button
              onClick={handleSubmit}
              disabled={loading}
              type="button"
              className={`w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 text-base font-medium text-white focus:outline-none focus:ring-2 focus:ring-offset-2 sm:ml-3 sm:w-auto sm:text-sm ${
                loading
                  ? 'bg-gray-400 cursor-not-allowed'
                  : 'bg-blue-600 hover:bg-blue-700 focus:ring-blue-500'
              }`}
            >
              {loading ? 'Saving...' : 'Save'}
            </button>
            <button
              onClick={onCancel}
              disabled={loading}
              type="button"
              className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Clear
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AddUser;
